import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, PERMISSION, TAG_COLOURS, can, inboxApi, useAuth, type AppliedTag, type Tag } from '@wa/shared';
import { TagChip } from '@/components/TagChip';

/**
 * Tags on one conversation: the chips in the panel, and the dialog behind the `+`.
 *
 * The dialog is a **centred modal with checkboxes and one Apply**, which is what
 * the reference does and is the right shape for the job. The version this replaced
 * applied a tag the instant you clicked it, from a dropdown inside a 290px column
 * — so tagging four conversations was four round trips, four list invalidations
 * and four chances to knock one off by mis-clicking, with no way to change your
 * mind before committing. Apply sends the difference in one go.
 *
 * Creating inline stays, for admins: the moment somebody needs a tag is while
 * reading the message that needs it, and a trip to a settings screen is where the
 * habit dies. Agents get the existing list only, so the vocabulary stays shared.
 */
export function TagEditor({ conversationId, applied, open, onOpenChange }: {
  conversationId: number;
  applied: AppliedTag[];
  /** Owned by the row this sits in, so one `+` drives one dialog. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const isAdmin = can(user, PERMISSION.tagsWrite);
  const appliedIds = useMemo(() => new Set(applied.map((t) => t.id)), [applied]);

  function refresh() {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    void queryClient.invalidateQueries({ queryKey: ['tags'] });
  }

  const fail = (e: unknown) => setError(e instanceof ApiError ? e.message : 'That did not save.');

  const remove = useMutation({
    mutationFn: (tagId: number) => inboxApi.removeTag(conversationId, tagId),
    onSuccess: refresh,
    onError: fail,
  });

  return (
    <section className="min-w-0">
      {/* No heading and no toggle of its own: both belong to the row this sits
          inside, and drawing a second "TAGS" under the first one is what happened
          the last time this component owned its own header. */}
      {applied.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {applied.map((tag) => (
            <TagChip key={tag.id} tag={tag} onRemove={() => remove.mutate(tag.id)} />
          ))}
        </div>
      ) : (
        <p className="text-small text-(--color-muted)">None yet.</p>
      )}

      {error && <p role="alert" className="mt-1.5 text-micro text-(--color-bad)">{error}</p>}

      {open && (
        <TagDialog
          conversationId={conversationId}
          appliedIds={appliedIds}
          isAdmin={isAdmin}
          onClose={() => onOpenChange(false)}
          onDone={() => { refresh(); onOpenChange(false); }}
        />
      )}
    </section>
  );
}

/** The name a tag can carry, and the number beside the search box. */
const MAX_NAME = 50;

function TagDialog({ conversationId, appliedIds, isAdmin, onClose, onDone }: {
  conversationId: number;
  appliedIds: Set<number>;
  isAdmin: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [draft, setDraft] = useState('');
  const [picked, setPicked] = useState<Set<number>>(new Set(appliedIds));
  const [managing, setManaging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tags = useQuery({ queryKey: ['tags'], queryFn: inboxApi.tags, staleTime: 300_000 });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const fail = (e: unknown) => setError(e instanceof ApiError ? e.message : 'That did not save.');
  const trimmed = draft.trim();
  const all = tags.data ?? [];
  const exact = all.some((t) => t.name.toLowerCase() === trimmed.toLowerCase());
  const shown = trimmed
    ? all.filter((t) => t.name.toLowerCase().includes(trimmed.toLowerCase()))
    : all;

  const create = useMutation({
    mutationFn: async (name: string) => {
      const colour = TAG_COLOURS[all.length % TAG_COLOURS.length]!;
      return inboxApi.createTag(name, colour);
    },
    // Ticked, not applied. Creating a tag inside a dialog whose whole promise is
    // that nothing lands until Apply must not be the one thing that lands early.
    onSuccess: (tag) => {
      setDraft('');
      setPicked((current) => new Set(current).add(tag.id));
      void tags.refetch();
    },
    onError: fail,
  });

  // One request per change, and none at all for a tag that was already where it
  // is now. Opening the dialog and pressing Apply must be a no-op.
  const apply = useMutation({
    mutationFn: async () => {
      const add = [...picked].filter((id) => !appliedIds.has(id));
      const drop = [...appliedIds].filter((id) => !picked.has(id));
      for (const id of add) await inboxApi.applyTag(conversationId, id);
      for (const id of drop) await inboxApi.removeTag(conversationId, id);
    },
    onSuccess: onDone,
    onError: fail,
  });

  const toggle = (id: number) => setPicked((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" role="dialog" aria-modal="true" aria-label="Add a tag">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/40" />

      <div className="relative flex max-h-[80vh] w-[min(34rem,100%)] flex-col overflow-hidden rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) shadow-(--shadow-pop)">
        <header className="flex shrink-0 items-center gap-3 border-b border-(--color-line) px-4 py-3">
          <h2 className="min-w-0 flex-1 text-lead font-semibold">{managing ? 'Manage tags' : 'Add a tag'}</h2>
          {isAdmin && !managing && (
            <button
              type="button"
              disabled={create.isPending || trimmed === '' || exact}
              onClick={() => create.mutate(trimmed)}
              // Disabled with a reason rather than hidden: an admin who has typed
              // a name that already exists needs to know that is why.
              title={trimmed === '' ? 'Type a name first' : exact ? 'That tag already exists' : undefined}
              className="tap text-small font-medium text-(--color-brand) hover:underline disabled:text-(--color-muted) disabled:no-underline"
            >
              Create new tag
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="tap grid size-7 shrink-0 place-items-center rounded text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)"
          >
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        {managing ? (
          <ManagePane tags={all} onChanged={() => void tags.refetch()} onError={fail} />
        ) : (
        <>
        <div className="shrink-0 px-4 pt-3">
          <div className="flex items-center gap-2 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2.5 focus-within:border-(--color-brand)">
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" className="shrink-0 text-(--color-muted)">
              <circle cx="9" cy="9" r="5.4" fill="none" stroke="currentColor" strokeWidth="1.6" />
              <path d="m13.2 13.2 3 3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <input
              autoFocus
              value={draft}
              maxLength={MAX_NAME}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && isAdmin && trimmed && !exact) create.mutate(trimmed);
              }}
              placeholder={isAdmin ? 'Search or create a tag…' : 'Search a tag…'}
              aria-label={isAdmin ? 'Search or create a tag' : 'Search a tag'}
              className="tap min-w-0 flex-1 bg-transparent py-2.5 text-body outline-none placeholder:text-(--color-muted)"
            />
            <span className="shrink-0 text-micro text-(--color-muted) tabular-nums">
              {draft.length}/{MAX_NAME}
            </span>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          {tags.isPending && <p className="px-2 py-6 text-center text-small text-(--color-muted)">Loading…</p>}

          {!tags.isPending && shown.length === 0 && (
            <p className="px-2 py-6 text-center text-small text-(--color-muted)">
              {all.length === 0
                ? 'No tags yet.'
                : `Nothing matches “${trimmed}”.`}
              {isAdmin && trimmed && !exact && ' Create it above.'}
            </p>
          )}

          {shown.map((tag) => (
            <label
              key={tag.id}
              className="tap flex cursor-pointer items-center gap-3 rounded px-2 py-2.5 hover:bg-(--color-sunken)"
            >
              <input
                type="checkbox"
                checked={picked.has(tag.id)}
                onChange={() => toggle(tag.id)}
                className="size-4 shrink-0 accent-(--color-brand)"
              />
              <span
                aria-hidden="true"
                className="size-2.5 shrink-0 rounded-full"
                style={{ background: tag.colour ?? '#94a3b8' }}
              />
              <span className="min-w-0 flex-1 truncate text-body">{tag.name}</span>
            </label>
          ))}
        </div>

        </>
        )}

        {error && <p role="alert" className="shrink-0 px-4 pb-1 text-micro text-(--color-bad)">{error}</p>}

        <footer className="flex shrink-0 items-center gap-3 border-t border-(--color-line) px-4 py-3">
          {/* A second pane in this dialog rather than a link to a screen. Two
              reasons: renaming and deleting tags had a complete API and no UI
              anywhere in the product — the third feature this milestone found
              shipped with no way in — and navigating away from a dialog whose
              whole promise is that nothing lands until Apply would throw away
              everything ticked. */}
          {isAdmin && (
            <button
              type="button"
              onClick={() => setManaging((v) => !v)}
              className="text-small font-medium text-(--color-brand) hover:underline"
            >
              {managing ? 'Back to tags' : 'Manage tags'}
            </button>
          )}
          <span className="flex-1" />
          <button
            type="button"
            disabled={apply.isPending || managing}
            onClick={() => apply.mutate()}
            className="tap rounded-(--radius-field) bg-(--color-brand) px-5 py-2 text-small font-medium text-(--color-brand-ink) disabled:opacity-60"
          >
            {apply.isPending ? 'Applying…' : 'Apply'}
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * Renaming and deleting the tenant's tags.
 *
 * Deleting says how many conversations carry the tag, because the count is the
 * only thing that distinguishes "a typo nobody used" from "the label six months
 * of reporting is built on", and the API cannot tell them apart for you.
 */
function ManagePane({ tags, onChanged, onError }: {
  tags: Tag[];
  onChanged: () => void;
  onError: (e: unknown) => void;
}) {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const [confirming, setConfirming] = useState<number | null>(null);

  const after = () => {
    setEditingId(null);
    setConfirming(null);
    onChanged();
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
  };

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => inboxApi.updateTag(id, { name }),
    onSuccess: after,
    onError,
  });

  const drop = useMutation({
    mutationFn: (id: number) => inboxApi.deleteTag(id),
    onSuccess: after,
    onError,
  });

  if (tags.length === 0) {
    return <p className="flex-1 px-4 py-8 text-center text-small text-(--color-muted)">No tags to manage yet.</p>;
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
      {tags.map((tag) => (
        <div key={tag.id} className="flex items-center gap-3 rounded px-2 py-2">
          <span
            aria-hidden="true"
            className="size-2.5 shrink-0 rounded-full"
            style={{ background: tag.colour ?? '#94a3b8' }}
          />

          {editingId === tag.id ? (
            <input
              autoFocus
              value={draft}
              maxLength={MAX_NAME}
              aria-label={`Rename ${tag.name}`}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && draft.trim()) rename.mutate({ id: tag.id, name: draft.trim() });
                if (event.key === 'Escape') setEditingId(null);
              }}
              className="tap min-w-0 flex-1 rounded border border-(--color-line) bg-(--color-surface) px-2 py-1 text-small outline-none focus:border-(--color-brand)"
            />
          ) : (
            <span className="min-w-0 flex-1 truncate text-body">{tag.name}</span>
          )}

          <span className="shrink-0 text-micro text-(--color-muted) tabular-nums">
            {tag.conversationCount} {tag.conversationCount === 1 ? 'chat' : 'chats'}
          </span>

          {editingId === tag.id ? (
            <button
              type="button"
              disabled={rename.isPending || draft.trim() === ''}
              onClick={() => rename.mutate({ id: tag.id, name: draft.trim() })}
              className="tap shrink-0 rounded px-2 py-1 text-micro font-medium text-(--color-brand) disabled:opacity-50"
            >
              Save
            </button>
          ) : (
            <button
              type="button"
              onClick={() => { setDraft(tag.name); setEditingId(tag.id); setConfirming(null); }}
              className="tap shrink-0 rounded px-2 py-1 text-micro font-medium text-(--color-muted) hover:text-(--color-ink)"
            >
              Rename
            </button>
          )}

          {confirming === tag.id ? (
            <button
              type="button"
              disabled={drop.isPending}
              onClick={() => drop.mutate(tag.id)}
              className="tap shrink-0 rounded px-2 py-1 text-micro font-semibold text-(--color-bad) disabled:opacity-50"
            >
              {tag.conversationCount > 0 ? `Remove from ${tag.conversationCount}?` : 'Sure?'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => { setConfirming(tag.id); setEditingId(null); }}
              className="tap shrink-0 rounded px-2 py-1 text-micro font-medium text-(--color-muted) hover:text-(--color-bad)"
            >
              Delete
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
